import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, Eye, Mail, Calendar,
  CheckCircle, Clock, AlertCircle, MessageCircle, Volume2, Send, UserPlus,
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { isUserCoach, checkCoachAIAccess } from '../../../lib/auth';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { fetchCoachConfig, fetchRespondentAccess, upsertRespondentAccess } from '../../../lib/ai-coach';
import { InviteRespondentModal } from '../../../components/coach/InviteRespondentModal';

interface Respondent {
  id: string;
  userId: string;
  email: string;
  name: string;
  createdAt: Date;
  hasCompletedAssessment: boolean;
  completedAt?: Date;
  hasAiAnalysis: boolean;
  aiAnalysisVisible: boolean;
  chatEnabled: boolean;
  voiceEnabled: boolean;
}

export function RespondentsListTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'completed' | 'pending'>('all');
  const [coachHasAIAccess, setCoachHasAIAccess] = useState(false);
  const [aiCoachEnabled, setAiCoachEnabled] = useState(false);
  const [currentCoachId, setCurrentCoachId] = useState<string | null>(null);
  const [assessmentCode, setAssessmentCode] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/tr/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/tr/login');
          return;
        }

        const aiAccess = await checkCoachAIAccess(user.id);
        setCoachHasAIAccess(aiAccess);

        const { data: coach } = await supabase
          .from('coaches')
          .select('id, assessment_code')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) {
          navigate('/tr/login');
          return;
        }

        setCurrentCoachId(coach.id);
        setAssessmentCode(coach.assessment_code || null);

        const [respondentsRes, aiConfig, aiAccessList] = await Promise.all([
          supabase
            .from('respondents')
            .select('id, user_id, created_at, ai_analysis_visible, users!respondents_user_id_fkey(full_name, email)')
            .eq('coach_id', coach.id),
          fetchCoachConfig(coach.id),
          fetchRespondentAccess(coach.id),
        ]);

        const respondentsData = respondentsRes.data;
        setAiCoachEnabled(aiConfig?.isEnabled || false);

        const accessMap = new Map(aiAccessList.map(a => [a.respondentId, a]));
        const userIds = (respondentsData || []).map(r => r.user_id);

        const completedResults = new Map<string, { completedAt: Date; hasAi: boolean }>();
        if (userIds.length > 0) {
          const { data: results } = await supabase
            .from('results')
            .select('user_id, completed_at, ai_analysis')
            .in('user_id', userIds);

          (results || []).forEach(result => {
            const existing = completedResults.get(result.user_id);
            const completedAt = result.completed_at ? new Date(result.completed_at) : null;
            if (completedAt && (!existing || completedAt > existing.completedAt)) {
              completedResults.set(result.user_id, {
                completedAt,
                hasAi: !!result.ai_analysis,
              });
            }
          });
        }

        const mapped = (respondentsData || []).map(r => {
          const resultInfo = completedResults.get(r.user_id);
          const access = accessMap.get(r.id);
          return {
            id: r.id,
            userId: r.user_id,
            email: (r.users as any)?.email || '',
            name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
            createdAt: new Date(r.created_at || Date.now()),
            hasCompletedAssessment: !!resultInfo,
            completedAt: resultInfo?.completedAt,
            hasAiAnalysis: resultInfo?.hasAi || false,
            aiAnalysisVisible: r.ai_analysis_visible || false,
            chatEnabled: access?.chatEnabled || false,
            voiceEnabled: access?.voiceEnabled || false,
          };
        }) as Respondent[];

        setRespondents(mapped);
      } catch (err) {
        console.error('Error loading respondents:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const triggerAiGeneration = async (userId: string) => {
    try {
      const { data: result } = await supabase
        .from('results')
        .select('id, scores, harmonic_scores, ai_analysis')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!result || result.ai_analysis) return;

      const { buildAIPrompt } = await import('../../../components/ai/buildAIPrompt');
      const scores = (result.scores as any[]) || [];
      const harmonicScores = (result.harmonic_scores as any[]) || [];
      const prompt = buildAIPrompt(
        scores,
        harmonicScores,
        'Bu kişinin potansiyelini, güçlü yönlerini ve gelişim alanlarını kapsamlı bir şekilde analiz et. Kişisel gelişim için spesifik öneriler dahil et. Tüm analizi açık, profesyonel ve doğal Türkçe ile yaz.',
        'tr'
      );

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) return;

      const data = await response.json();
      await supabase
        .from('results')
        .update({
          ai_analysis: data.analysis,
          ai_analysis_generated_at: new Date().toISOString(),
        })
        .eq('id', result.id);
    } catch (err) {
      console.error('Error auto-generating AI analysis:', err);
    }
  };

  const toggleAiVisibility = async (respondent: Respondent) => {
    const newValue = !respondent.aiAnalysisVisible;

    setTogglingIds(prev => new Set(prev).add(respondent.id));

    try {
      const { error } = await supabase
        .from('respondents')
        .update({ ai_analysis_visible: newValue })
        .eq('id', respondent.id);

      if (error) throw error;

      setRespondents(prev =>
        prev.map(r => r.id === respondent.id ? { ...r, aiAnalysisVisible: newValue } : r)
      );
    } catch (err) {
      console.error('Error toggling AI visibility:', err);
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(respondent.id);
        return next;
      });
    }
  };

  const toggleAiCoachAccess = async (respondent: Respondent, field: 'chatEnabled' | 'voiceEnabled') => {
    if (!currentCoachId) return;
    const newValue = !respondent[field];

    setTogglingIds(prev => new Set(prev).add(`${respondent.id}-${field}`));

    try {
      await upsertRespondentAccess(currentCoachId, respondent.id, {
        chatEnabled: field === 'chatEnabled' ? newValue : respondent.chatEnabled,
        voiceEnabled: field === 'voiceEnabled' ? newValue : respondent.voiceEnabled,
        maxChatMessages: 0,
        maxVoiceMinutes: 0,
        maxTokensPerMonth: 0,
      });

      setRespondents(prev =>
        prev.map(r => r.id === respondent.id ? { ...r, [field]: newValue } : r)
      );
    } catch (err) {
      console.error('Error toggling AI Coach access:', err);
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(`${respondent.id}-${field}`);
        return next;
      });
    }
  };

  const resendCredentials = async (respondent: Respondent) => {
    if (!confirm(`${respondent.name} kullanıcısının şifresi sıfırlanacak ve yeni giriş bilgileri kendisine gönderilecektir. Devam etmek istiyor musunuz?`)) return;
    setResendingIds(prev => new Set(prev).add(respondent.id));
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'resend-credentials-respondent-tr',
            recipientEmail: respondent.email,
            recipientName: respondent.name,
            userId: respondent.userId,
            loginUrl: 'https://app.humanpotentialos.com/tr/login',
          }),
        }
      );
      if (response.ok) {
        alert('Giris bilgileri basariyla gonderildi!');
      } else {
        const err = await response.json();
        alert('Gonderilemedi: ' + (err.error || 'Bilinmeyen hata'));
      }
    } catch (err) {
      alert('Giris bilgileri gonderilemedi');
    } finally {
      setResendingIds(prev => {
        const next = new Set(prev);
        next.delete(respondent.id);
        return next;
      });
    }
  };

  const filterLabels: Record<string, string> = {
    all: 'tümü',
    completed: 'tamamlanan',
    pending: 'bekleyen',
  };

  const filteredRespondents = respondents.filter(r => {
    const matchesSearch =
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.email.toLowerCase().includes(searchQuery.toLowerCase());

    if (filter === 'completed') return matchesSearch && r.hasCompletedAssessment;
    if (filter === 'pending') return matchesSearch && !r.hasCompletedAssessment;
    return matchesSearch;
  });

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Danışanlar yükleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <AnimatePresence>
        {showInviteModal && assessmentCode && (
          <InviteRespondentModal
            assessmentCode={assessmentCode}
            coachName={user?.user_metadata?.full_name || user?.email || ''}
            onClose={() => setShowInviteModal(false)}
            locale="tr"
          />
        )}
      </AnimatePresence>
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-warm-500 rounded-xl shadow-soft">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">Danışanlar</h1>
                <p className="freud-text-sm text-chocolate-500">{respondents.length} toplam danışan</p>
              </div>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="freud-btn-primary flex items-center gap-2 flex-shrink-0"
            >
              <UserPlus className="w-4 h-4" />
              Danışan Davet Et
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="freud-card-elevated mb-6">
          <div className="p-4 border-b border-cream-200 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Danışan ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'completed', 'pending'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                    filter === f
                      ? 'bg-sage-600 text-white'
                      : 'bg-cream-200 text-chocolate-600 hover:bg-cream-300'
                  }`}
                >
                  {filterLabels[f]}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Danışan</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">E-posta</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Durum</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Katılım Tarihi</th>
                  {coachHasAIAccess && (
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">YZ Analizi</th>
                  )}
                  {aiCoachEnabled && (
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">YZ Koc</th>
                  )}
                  <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {filteredRespondents.map((respondent) => (
                  <tr key={respondent.id} className="hover:bg-cream-50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-chocolate-900">{respondent.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Mail className="w-4 h-4" />
                        {respondent.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {respondent.hasCompletedAssessment ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage-100 text-sage-700 rounded-full text-sm">
                          <CheckCircle className="w-3 h-3" />
                          Tamamlandı
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warm-100 text-warm-700 rounded-full text-sm">
                          <Clock className="w-3 h-3" />
                          Bekliyor
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Calendar className="w-4 h-4" />
                        {respondent.createdAt.toLocaleDateString()}
                      </div>
                    </td>
                    {coachHasAIAccess && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          {respondent.hasCompletedAssessment ? (
                            <button
                              onClick={() => toggleAiVisibility(respondent)}
                              disabled={togglingIds.has(respondent.id)}
                              className="group flex items-center gap-2"
                            >
                              <div
                                className={`relative w-11 h-6 rounded-full transition-colors ${
                                  respondent.aiAnalysisVisible ? 'bg-sage-500' : 'bg-gray-300'
                                } ${togglingIds.has(respondent.id) ? 'opacity-60' : ''}`}
                              >
                                <div
                                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                                    respondent.aiAnalysisVisible ? 'translate-x-[22px]' : 'translate-x-0.5'
                                  }`}
                                />
                              </div>
                              <span className={`text-xs font-medium ${
                                respondent.aiAnalysisVisible ? 'text-sage-600' : 'text-gray-400'
                              }`}>
                                {respondent.aiAnalysisVisible ? 'Gorunur' : 'Gizli'}
                              </span>
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">--</span>
                          )}
                        </div>
                      </td>
                    )}
                    {aiCoachEnabled && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => toggleAiCoachAccess(respondent, 'chatEnabled')}
                            disabled={togglingIds.has(`${respondent.id}-chatEnabled`)}
                            className="group flex items-center gap-1.5"
                            title="Toggle chat access"
                          >
                            <MessageCircle className={`w-4 h-4 ${respondent.chatEnabled ? 'text-sage-600' : 'text-gray-300'}`} />
                            <div
                              className={`relative w-9 h-5 rounded-full transition-colors ${
                                respondent.chatEnabled ? 'bg-sage-500' : 'bg-gray-300'
                              } ${togglingIds.has(`${respondent.id}-chatEnabled`) ? 'opacity-60' : ''}`}
                            >
                              <div
                                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                                  respondent.chatEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                                }`}
                              />
                            </div>
                          </button>
                          <button
                            onClick={() => toggleAiCoachAccess(respondent, 'voiceEnabled')}
                            disabled={togglingIds.has(`${respondent.id}-voiceEnabled`)}
                            className="group flex items-center gap-1.5"
                            title="Toggle voice access"
                          >
                            <Volume2 className={`w-4 h-4 ${respondent.voiceEnabled ? 'text-warm-600' : 'text-gray-300'}`} />
                            <div
                              className={`relative w-9 h-5 rounded-full transition-colors ${
                                respondent.voiceEnabled ? 'bg-warm-500' : 'bg-gray-300'
                              } ${togglingIds.has(`${respondent.id}-voiceEnabled`) ? 'opacity-60' : ''}`}
                            >
                              <div
                                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                                  respondent.voiceEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                                }`}
                              />
                            </div>
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => resendCredentials(respondent)}
                          disabled={resendingIds.has(respondent.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-warm-600 hover:bg-warm-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Giriş Bilgilerini Yenile"
                        >
                          <Send className="w-4 h-4" />
                          <span className="hidden sm:inline text-sm">Giriş Bilgilerini Yenile</span>
                        </button>
                        {respondent.hasCompletedAssessment && (
                          <Link
                            to={`/tr/coach/results/${respondent.userId}`}
                            className="flex items-center gap-2 px-3 py-1.5 text-sage-600 hover:bg-sage-50 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Sonuclari Gor
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRespondents.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Danışan Bulunamadı
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
